import { useState } from 'react';
import { Icon } from '@iconify/react';
import { Chip } from '@nextui-org/react';
import {
  CtyButton,
  AnalystChart,
  AnalystChartConfig,
  AnalystGridConfig,
  AnalystMetricConfig,
  AnalystResponse,
  isEmptyPayload,
  normalizeResponse,
} from '@circuitry-ai/doc-ui';
import ReactMarkdown from 'react-markdown';
import { useTranslation } from 'react-i18next';
import { AnalystMetrics } from './AnalystMetrics';
import { AnalystGrid } from './AnalystTable';

interface AnalystCardProps {
  readonly id: string;
  readonly question: string;
  readonly useCache?: boolean;
  readonly response?: AnalystResponse;
  readonly error?: string;
  readonly loading?: boolean;
  /** When true the outer card border is removed; items are separated by a divider instead. */
  readonly unstyled?: boolean;
  readonly onDrilldown: (question: string) => void;
}

function ResponseTypeChip({ responseType }: { readonly responseType?: string }) {
  const { t } = useTranslation();
  if (responseType === 'generated') {
    return (
      <Chip size="sm" color="warning" variant="flat">
        {t('Generated response')}
      </Chip>
    );
  }
  if (responseType === 'cached') {
    return (
      <Chip size="sm" color="success" variant="flat">
        {t('Cached response')}
      </Chip>
    );
  }
  return null;
}

function ShowSql({ sql }: { readonly sql: string }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-3">
      <button
        type="button"
        className="inline-flex items-center gap-1 text-sm font-medium text-default-500 hover:text-primary"
        onClick={() => setOpen((current) => !current)}
      >
        <Icon icon={open ? 'mdi:chevron-down' : 'mdi:chevron-right'} width={14} />
        {open ? t('Hide SQL') : t('Show SQL')}
      </button>
      {open ? (
        <pre className="mt-2 overflow-x-auto rounded-md border border-default-200 bg-content2 p-3 text-xs leading-relaxed text-foreground" style={{ fontFamily: "'JetBrains Mono', 'Fira Code', monospace" }}>
          <code>{sql}</code>
        </pre>
      ) : null}
    </div>
  );
}

function FeedbackBar() {
  const { t } = useTranslation();
  const [vote, setVote] = useState<'like' | 'dislike' | null>(null);
  return (
    <div className="mt-3 flex items-center gap-2 border-t border-default-200 pt-3">
      <span className="text-xs text-default-400">{t('Was this helpful?')}</span>
      <div className="inline-flex overflow-hidden rounded-full border border-default-200">
        <button
          type="button"
          className={`inline-flex h-7 w-9 items-center justify-center border-r border-default-200 ${vote === 'like' ? 'bg-success/10 text-success' : 'text-default-500 hover:bg-default-100'}`}
          onClick={() => setVote(vote === 'like' ? null : 'like')}
          aria-label={t('Helpful')}
        >
          <Icon icon="solar:like-outline" width={15} />
        </button>
        <button
          type="button"
          className={`inline-flex h-7 w-9 items-center justify-center ${vote === 'dislike' ? 'bg-danger/10 text-danger' : 'text-default-500 hover:bg-default-100'}`}
          onClick={() => setVote(vote === 'dislike' ? null : 'dislike')}
          aria-label={t('Not helpful')}
        >
          <Icon icon="solar:dislike-outline" width={15} />
        </button>
      </div>
      <span className="ml-auto text-[10px] uppercase tracking-wide text-default-300">{t('Sprint 1 · Stub')}</span>
    </div>
  );
}

/** Routes the chart-area zone to the correct renderer based on the block type. */
function ChartArea({
  type,
  config,
  data,
}: {
  readonly type?: string;
  readonly config?: AnalystChartConfig | AnalystMetricConfig | AnalystGridConfig;
  readonly data?: Record<string, unknown>[];
}) {
  // metric blocks may arrive with no `config` (sparse payload — contract §6).
  // Build the metric config from `data` directly so the tile still renders.
  if (type === 'metric') {
    const metricConfig = { ...config, tiles: data } as AnalystMetricConfig;
    return <AnalystMetrics config={metricConfig} />;
  }
  if (!config) return null;
  if (type === 'grid') {
    const gridConfig = { ...config, rows: data } as unknown as AnalystGridConfig;
    return <AnalystGrid config={gridConfig} />;
  }
  const chartConfig = { ...config, data } as AnalystChartConfig;
  return <AnalystChart config={chartConfig} />;
}

const SKELETON_BAR_HEIGHTS = [40, 70, 55, 85, 60, 48, 76];

/**
 * Animated loading placeholder that mimics a chart card while the analyst API
 * resolves. A translucent gradient sweeps left-to-right (shimmer) over muted
 * bars so it's clearly readable as "loading".
 */
function ChartSkeleton() {
  const { t } = useTranslation();
  return (
    <div className="relative h-[240px] overflow-hidden rounded-md border border-default-200 bg-content1 p-4">
      <div className="flex flex-col gap-3">
        <div className="h-4 w-2/5 rounded bg-default-100" />
        <div className="h-3 w-1/4 rounded bg-default-100" />
      </div>
      <div className="mt-6 flex h-[120px] items-end gap-3">
        {SKELETON_BAR_HEIGHTS.map((barHeight) => (
          <div
            key={`skeleton-${barHeight}`}
            className="flex-1 rounded-t bg-default-100"
            style={{ height: `${barHeight}%` }}
          />
        ))}
      </div>
      <div className="mt-4 h-3 w-3/4 rounded bg-default-100" />
      {/* Moving highlight sweep — the shimmer. */}
      <div className="pointer-events-none absolute inset-0 -translate-x-full animate-shimmer bg-gradient-to-r from-transparent via-white/70 to-transparent" />
      <span className="sr-only">{t('Loading analysis…')}</span>
    </div>
  );
}

function StateBanner({ state, detail }: { readonly state: string; readonly detail?: string }) {
  const { t } = useTranslation();
  if (state === 'loading') {
    return <ChartSkeleton />;
  }
  if (state === 'empty') {
    return (
      <div className="flex h-[180px] flex-col items-center justify-center rounded-md border border-dashed border-default-300 bg-default-50 text-center">
        <Icon icon="mdi:database-off-outline" width={28} className="text-default-300" />
        <div className="mt-2 text-sm font-medium text-default-600">{t('No data found for this query')}</div>
        <div className="mt-1 text-xs text-default-400">{t('Try different question or remove a filter.')}</div>
      </div>
    );
  }
  return (
    <div className="flex flex-col items-center justify-center rounded-md border border-danger bg-danger/10 p-5 text-center">
      <Icon icon="mdi:alert-circle-outline" width={28} className="text-danger" />
      <div className="mt-2 text-sm font-medium text-danger">{t("We couldn't run that analysis.")}</div>
      {detail ? (
        <div className="mt-1 max-w-md whitespace-pre-wrap break-words px-2 text-xs leading-relaxed text-danger">
          {detail}
        </div>
      ) : null}
    </div>
  );
}

/** Renders the chart-zone body: markdown body, chart router, or a state banner. */
function CardBody({
  type,
  effectiveState,
  payload,
  error,
}: {
  readonly type?: string;
  readonly effectiveState: string;
  readonly payload: ReturnType<typeof normalizeResponse>['payload'];
  readonly error?: string;
}) {
  if (effectiveState === 'success') {
    if (type === 'markdown') {
      // `markdown-body` (github-markdown-css) applies a dark canvas background in
      // OS dark mode; force a transparent bg + dark text so it reads on the card.
      return (
        <div
          className="markdown-body overflow-x-auto text-sm"
          style={{ backgroundColor: 'transparent', color: '#1f2937' }}
        >
          <ReactMarkdown>{String(payload.markdown ?? '')}</ReactMarkdown>
        </div>
      );
    }
    return <ChartArea type={type} config={payload.config} data={payload.data as Record<string, unknown>[] | undefined} />;
  }
  return <StateBanner state={effectiveState} detail={error} />;
}

/**
 * The single universal AnalystCard. Every analyst response — any of the 17
 * chart types, metric tiles, grids, or markdown — renders through this one
 * component, across all four UI states (contract §0–§3).
 */
export function AnalystCard({ id, question, useCache, response, error, loading, unstyled, onDrilldown }: AnalystCardProps) {
  const { t } = useTranslation();
  const { type, payload, drilldowns } = normalizeResponse(response);

  let effectiveState: 'success' | 'empty' | 'error' | 'loading';
  if (loading) effectiveState = 'loading';
  else if (error) effectiveState = 'error';
  else if (isEmptyPayload(type, payload)) effectiveState = 'empty';
  else effectiveState = 'success';

  const resolvedQuery = payload.query ?? question;
  const sessionId = typeof response?.session_id === 'string' ? response.session_id : undefined;
  const responseType = typeof response?.response_type === 'string' ? response.response_type : undefined;
  const responseTime = typeof response?.response_time === 'number' ? response.response_time : undefined;
  const infoTooltipParts: string[] = [];
  if (sessionId) infoTooltipParts.push(`${t('Session')}: ${sessionId}`);
  if (useCache !== undefined) infoTooltipParts.push(`${t('Cache')}: ${useCache ? t('on') : t('off')}`);
  const infoTooltipContent = infoTooltipParts.join(', ');

  return (
    <div className={unstyled ? 'border-b border-default-100 py-4 last:border-b-0' : 'mb-3 rounded-xl border border-default-200 bg-content1 p-4'}>
      {/* Zone 1 — Query echo */}
      <div className="mb-4 flex flex-wrap items-center gap-1.5 text-sm text-default-500">
        <span>{t('Q: {{question}}', { question: resolvedQuery })}</span>
        {infoTooltipContent ? (
          <CtyButton tooltipContent={infoTooltipContent} toolTipColor="warning" className="h-[32px]" isIconOnly radius="full" size="sm" variant="light">
            <div className="relative">
              <Icon icon="ph:info-light" width="1.2rem" height="1.1rem" style={{ color: '#afa4a4' }} />
            </div>
          </CtyButton>
        ) : null}
      </div>

      {/* Zone 2 — Card title */}
      {effectiveState === 'success' && (payload.title || responseType) ? (
        <div className="mb-2 flex items-center justify-between gap-3">
          {payload.title ? (
            <div className="min-w-0 flex-1 text-sm font-semibold text-primary" style={{ fontFamily: 'Oswald, sans-serif' }}>
              {payload.title}
            </div>
          ) : (
            <span className="flex-1" />
          )}
          <ResponseTypeChip responseType={responseType} />
        </div>
      ) : null}

      {/* Zone 2 — Chart area / states */}
      <CardBody type={type} effectiveState={effectiveState} payload={payload} error={error} />

      {/* Zone 4 — Show SQL */}
      {effectiveState === 'success' && payload.sql ? <ShowSql sql={payload.sql} /> : null}

      {/* Zone 5 — Narrative */}
      {effectiveState === 'success' && payload.narrative ? (
        <p className="mt-3 text-sm leading-relaxed text-default-700">{payload.narrative}</p>
      ) : null}

      {responseTime ? (
        <div className="mt-2 text-sm text-default-700">
          {t('Answered in {{responseTime}} seconds', { responseTime: (responseTime).toFixed(2) })}
        </div>
      ) : null}

      {/* Zone 6 — Drilldown pills */}
      {effectiveState === 'success' && drilldowns.length > 0 ? (
        <div className="pt-3">
          <div className="mb-3 text-sm font-medium tracking-wide text-default-500">{t('Ask Next')}</div>
          <div className="flex flex-wrap gap-2">
            {drilldowns.map((drilldownQuestion) => (
              <button
                key={`${id}-${drilldownQuestion}`}
                type="button"
                className="rounded-full bg-primary/10 px-3 py-1.5 text-xs text-primary transition-colors hover:bg-primary/20"
                onClick={() => onDrilldown(drilldownQuestion)}
              >
                {drilldownQuestion}
              </button>
            ))}
          </div>
        </div>
      ) : null}

      {/* Zone 7 — Feedback bar (reserved / stub) */}
      {/* <FeedbackBar /> */}
    </div>
  );
}

export default AnalystCard;
